import React from 'react';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { JoyAiV2 } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { AiBorderBox } from '@shared/components/AiBorderBox';
import { AiGradientText } from '@shared/components/AiGradientText';

// ─── Sub-components ──────────────────────────────────────────────────────────

type AiSuggestProps = Readonly<{
  children: React.ReactNode;
  showIcon?: boolean;
  onClick: () => void;
}>;

const AiSuggest: React.FC<AiSuggestProps> = ({ children, showIcon = true, onClick }) => (
  <ButtonV2
    variant="ghost"
    intent="neutral"
    onClick={onClick}
    padding={0}
    minWidth="auto"
    height={pxToRem(32)}
    _hover={{ backgroundColor: 'transparent' }}
  >
    <AiBorderBox
      width="fit-content"
      borderRadius={32}
      padding={`${pxToRem(10)} ${pxToRem(16)}`}
      borderWidth={1}
    >
      {showIcon && <JoyAiV2 size={24} />}
      <AiGradientText>{children}</AiGradientText>
    </AiBorderBox>
  </ButtonV2>
);

AiSuggest.displayName = 'TabSectionHeader.AiSuggest';

type ActionProps = Readonly<{
  children: React.ReactNode;
  intent?: 'neutral' | 'destructive';
  variant?: 'solid' | 'outline';
  isDisabled?: boolean;
  ariaLabel?: string;
  paddingX?: number;
  border?: string;
  borderColor?: string;
  onClick: () => void;
}>;

const Action: React.FC<ActionProps> = ({ children, intent = 'neutral', variant = 'solid', isDisabled, ariaLabel, paddingX = 6, border, borderColor, onClick }) => (
  <ButtonV2
    intent={intent}
    variant={variant}
    shape="rounded"
    size="sm"
    onClick={onClick}
    color={variant === 'solid' ? 'white' : undefined}
    height={pxToRem(32)}
    paddingX={paddingX}
    disabled={isDisabled}
    aria-label={ariaLabel}
    border={border}
    borderColor={borderColor}
  >
    {children}
  </ButtonV2>
);

Action.displayName = 'TabSectionHeader.Action';

type GhostProps = Readonly<{
  children: React.ReactNode;
  isDisabled?: boolean;
  paddingX?: number;
  onClick: () => void;
}>;

const Ghost: React.FC<GhostProps> = ({ children, isDisabled, paddingX = 4, onClick }) => (
  <ButtonV2
    variant="ghost"
    intent="neutral"
    size="sm"
    onClick={onClick}
    disabled={isDisabled}
    height={pxToRem(32)}
    paddingX={paddingX}
  >
    {children}
  </ButtonV2>
);

Ghost.displayName = 'TabSectionHeader.Ghost';

// ─── Root ────────────────────────────────────────────────────────────────────

type TabSectionHeaderProps = Readonly<{
  title?: string;
  children?: React.ReactNode;
}>;

interface TabSectionHeaderComponent
  extends React.FC<TabSectionHeaderProps>,
    Readonly<{
      AiSuggest: typeof AiSuggest;
      Action: typeof Action;
      Ghost: typeof Ghost;
    }> {}

const TabSectionHeaderRoot: React.FC<TabSectionHeaderProps> = ({ title, children }) => (
  <Flex
    alignItems="center"
    justifyContent="space-between"
    width="100%"
    position="sticky"
    top={0}
    zIndex={2}
    backgroundColor="white"
    paddingY={3}
    paddingX={{ _: 6, sm2: 8 }}
  >
    {title ? (
      <TextV2
        typographyVariant="hed3"
        fontWeight="medium"
        color="mono14"
      >
        {title}
      </TextV2>
    ) : null}
    <Flex
      alignItems="center"
      gap={3}
      flexWrap="wrap"
      justifyContent="flex-end"
      rowGap={2}
      marginLeft={title ? undefined : 'auto'}
    >
      {children}
    </Flex>
  </Flex>
);

const TabSectionHeader = Object.assign(TabSectionHeaderRoot, {
  AiSuggest,
  Action,
  Ghost,
}) as TabSectionHeaderComponent;

export default TabSectionHeader;
